import { gql } from '../../../core/graphql-client.js';
import type { Quarter, Person, Saturday, AvailabilityEntry, AvailabilityStatus, AvailabilityStats } from '../../../types.js';

export class AvailabilityMatrixPage extends HTMLElement {
  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie macierzy..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const qData = await gql.query<{ quarters: Quarter[] }>(`
        query { quarters { year quarterNum label isActive } }
      `);
      const quarter = qData.quarters.find(q => q.isActive);
      if (!quarter) {
        this.innerHTML = '<div class="page"><p>Brak aktywnego kwartału.</p></div>';
        return;
      }

      const mData = await gql.query<{
        availabilityMatrix: {
          people: Person[];
          saturdays: Saturday[];
          entries: AvailabilityEntry[];
          stats: AvailabilityStats;
        }
      }>(`
        query($year: Int!, $qn: Int!) {
          availabilityMatrix(year: $year, quarterNum: $qn) {
            people { id displayName }
            saturdays { date isSkipped }
            entries { personId saturdayDate status }
            stats { total responded pending pendingPeople }
          }
        }
      `, { year: quarter.year, qn: quarter.quarterNum });

      this.renderMatrix(quarter.label, mData.availabilityMatrix);
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private renderMatrix(
    quarterLabel: string,
    matrix: { people: Person[]; saturdays: Saturday[]; entries: AvailabilityEntry[]; stats: AvailabilityStats }
  ): void {
    const { people, saturdays, entries, stats } = matrix;
    const activeSaturdays = saturdays.filter(s => !s.isSkipped);

    // Build lookup: personId → saturdayDate → status
    const lookup = new Map<string, AvailabilityStatus>();
    for (const e of entries) {
      lookup.set(`${e.personId}_${e.saturdayDate}`, e.status);
    }

    const formatDateShort = (iso: string) => {
      const d = new Date(iso);
      return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    };

    const pct = stats.total > 0 ? Math.round((stats.responded / stats.total) * 100) : 0;

    this.innerHTML = `
      <div class="page matrix-page">
        <div class="page-header">
          <h1>Macierz — ${quarterLabel}</h1>
        </div>

        <div class="matrix-stats">
          Odpowiedzi: <strong>${stats.responded}/${stats.total}</strong> (${pct}%)
          ${stats.pending > 0 ? `<span class="matrix-pending">Czekamy na: ${stats.pendingPeople.join(', ')}</span>` : ''}
        </div>

        <div class="matrix-legend">
          <span class="legend-item"><span class="legend-dot legend-available"></span> Dostępny</span>
          <span class="legend-item"><span class="legend-dot legend-unavailable"></span> Niedostępny</span>
          <span class="legend-item"><span class="legend-dot legend-unknown"></span> Nie wiem / brak</span>
        </div>

        <div class="matrix-table-wrap">
          <table class="matrix-table">
            <thead>
              <tr>
                <th class="col-name">Osoba</th>
                ${activeSaturdays.map(s => `<th class="col-sat">${formatDateShort(s.date)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${people.map(person => `
                <tr>
                  <td class="col-name">${person.displayName}</td>
                  ${activeSaturdays.map(sat => {
                    const status = lookup.get(`${person.id}_${sat.date}`) ?? 'UNKNOWN';
                    return `<td class="col-sat cell-${status.toLowerCase()}" title="${person.displayName} — ${formatDateShort(sat.date)}: ${this.statusLabel(status)}">${this.statusIcon(status)}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
      <style>
        .matrix-page { max-width: 100%; }
        .matrix-stats { font-size: .875rem; margin-bottom: var(--space-sm); }
        .matrix-pending { display: block; font-size: .8rem; color: var(--color-on-surface-muted); margin-top: var(--space-xs); }
        .matrix-legend { display: flex; gap: var(--space-md); margin-bottom: var(--space-md); font-size: .8rem; }
        .legend-item { display: flex; align-items: center; gap: 4px; }
        .legend-dot { width: 14px; height: 14px; border-radius: 3px; }
        .legend-available { background: var(--color-success-bg, #e8f5e9); border: 1px solid var(--color-success, #4caf50); }
        .legend-unavailable { background: var(--color-error-bg, #fdecea); border: 1px solid var(--color-error, #d32f2f); }
        .legend-unknown { background: var(--color-surface-container); border: 1px solid var(--color-surface-dim); }
        .matrix-table-wrap { overflow-x: auto; margin: 0 calc(-1 * var(--space-md)); padding: 0 var(--space-md); }
        .matrix-table { border-collapse: collapse; font-size: .75rem; }
        .matrix-table th, .matrix-table td {
          padding: 4px 6px;
          border: 1px solid var(--color-surface-container);
          text-align: center;
          white-space: nowrap;
        }
        .matrix-table th {
          background: var(--color-surface-dim);
          font-weight: 600;
          position: sticky;
          top: 0;
        }
        .col-name {
          text-align: left;
          position: sticky;
          left: 0;
          background: var(--color-surface);
          z-index: 2;
          min-width: 100px;
          font-weight: 500;
        }
        thead .col-name { z-index: 3; background: var(--color-surface-dim); }
        .col-sat { min-width: 40px; }
        .cell-available { background: var(--color-success-bg, #e8f5e9); color: var(--color-success, #4caf50); }
        .cell-unavailable { background: var(--color-error-bg, #fdecea); color: var(--color-error, #d32f2f); }
        .cell-unknown { background: var(--color-surface-container); color: var(--color-on-surface-muted); }
        .error-text { color: var(--color-error); }
      </style>
    `;
  }

  private statusIcon(status: AvailabilityStatus | string): string {
    switch (status) {
      case 'AVAILABLE': return '✓';
      case 'UNAVAILABLE': return '✗';
      default: return '?';
    }
  }

  private statusLabel(status: AvailabilityStatus | string): string {
    switch (status) {
      case 'AVAILABLE': return 'Dostępny';
      case 'UNAVAILABLE': return 'Niedostępny';
      default: return 'Nie wiem';
    }
  }
}

customElements.define('availability-matrix-page', AvailabilityMatrixPage);
